import React, { useCallback, useRef, useMemo } from "react";
import { Button } from 'devextreme-react/button';
import {
    Column,
    DataGrid,
    Scrolling,
    FilterRow,
    Editing,
    Export,
    Selection,
    Toolbar as DataGridToolbar,
    Item as DataGridToolbarItem,
    RequiredRule,
    StateStoring,
    ColumnChooser
} from "devextreme-react/data-grid";
import { createStore } from 'devextreme-aspnet-data-nojquery';
import axios from "axios";
import notify from 'devextreme/ui/notify';
import { confirm } from 'devextreme/ui/dialog';
import RenderSelectBoxCell from '../../../../common/RenderCell';
import GridLayoutManager from '../../../../gridLayoutCustomization/GridLayoutManager';
import { GridLayout } from '../../../../common/GridLayout';
import data from "../../../../assets/apiRoutes.json";

// Transaction Charges Grid Component
const TransactionChargesGrid = ({ transactionHeaderId, accessToken, chargesGridRef, selectedItemKeys, setSelectedItemKeys, currentLayout, setCurrentLayout }) => {
    const gridKey = "A1B2C3D4-E5F6-7890-ABCD-EF1234567890";
    const isFormLoadRef = useRef(true);
    
    const url = "wm/TransactionCharge/GetTransactionChargeDetails";
    const updateUrl = "wm/TransactionCharge/UpdateTransactionChargeDetails";
    const insertUrl = "wm/TransactionCharge/AddTransactionChargeDetails";
    const deleteUrl = "wm/TransactionCharge/DeleteTransactionChargeDetails";

    const dataSource = useMemo(() => createStore({
        key: 'rowid',
        loadUrl: `${window._env_.REACT_APP_APIS}/${url}`,
        insertUrl: `${window._env_.REACT_APP_APIS}/${insertUrl}`,
        updateUrl: `${window._env_.REACT_APP_APIS}/${updateUrl}`,
        deleteUrl: `${window._env_.REACT_APP_APIS}/${deleteUrl}`,
        onBeforeSend: function (method, ajaxOptions) {
            ajaxOptions.headers = {
                Authorization: `Bearer ${accessToken}`,
                Key: transactionHeaderId,
                "FormLoad": isFormLoadRef.current,
            };
        },
        onUpdated: function (e) {
            isFormLoadRef.current = false;
        },
        onInserted: function (e) {
            isFormLoadRef.current = false;
        }
    }), [transactionHeaderId, accessToken]);

    const saveState = useCallback((state) => { 
        GridLayout.sendStorageRequest(gridKey, "POST", accessToken, state, currentLayout); 
    }, [gridKey, currentLayout, accessToken]);
    
    const loadState = useCallback(() => { 
        return GridLayout.sendStorageRequest(gridKey, "GET", accessToken, null, currentLayout); 
    }, [currentLayout, accessToken, gridKey]);

    const handleCellValueChanged = (data) => {
        setSelectedItemKeys(data.selectedRowKeys);
    };

    const deleteRecords = useCallback(() => {
        let result = confirm("<i>Are you sure you want to delete selected records?</i>", "Delete");
        result.then((dialogResult) => {
            if (dialogResult) {
                chargesGridRef.current.instance().beginCustomLoading("Deleting");
                axios.post(
                    `${window._env_.REACT_APP_APIS}/${deleteUrl}`,
                    selectedItemKeys,
                    {
                        headers: {
                            "Content-Type": "application/json",
                            access_token: accessToken,
                            Authorization: `Bearer ${accessToken}`
                        },
                    }
                )
                .then((res) => {
                    chargesGridRef.current.instance().endCustomLoading();
                    chargesGridRef.current.instance().refresh();
                })
                .catch((err) => {
                    chargesGridRef.current.instance().endCustomLoading();
                    notify(
                        {
                            message: "Error deleting charges",
                            width: 300,
                        },
                        'error',
                        3000,
                    );
                });
            }
        });
    }, [selectedItemKeys, accessToken, deleteUrl, chargesGridRef]);

    const onContentReady = (e) => {
        if (!e.component._hasLoaded) {
            e.component._hasLoaded = true;
            var selection = e.component.getSelectedRowKeys();
            if (selection.length == 0) {
                e.component.selectRowsByIndexes([0]);
            }
        }
    };

    const onInitNewRow = (e) => {
        e.data.rTransactionheader = transactionHeaderId;
        e.data.chargetype = 'T'; // Transaction charge type
    };

    if (!transactionHeaderId || transactionHeaderId === '00000000-0000-0000-0000-000000000000') {
        return <div style={{ padding: '20px', textAlign: 'center' }}>Please save the transaction first to view charges.</div>;
    }

    return (
        <div>
            <DataGrid
                onExporting={(e) => {
                    if (window.handleDevExtremeDataGridExport) {
                        window.handleDevExtremeDataGridExport(e);
                    }
                }}
                dataSource={dataSource}
                ref={chargesGridRef}
                selectedRowKeys={selectedItemKeys}
                showBorders={true}
                rowAlternationEnabled={true}
                allowColumnReordering={true}
                allowColumnResizing={true}
                columnAutoWidth={true}
                onSelectionChanged={handleCellValueChanged}
                onContentReady={onContentReady}
                onRowInserting={onInitNewRow}
                height="70vh"
            >
                <StateStoring
                    enabled={true}
                    type="custom"
                    customLoad={loadState}
                    storageKey={gridKey}
                    customSave={saveState}
                />
                <Scrolling mode="standard" />
                <Selection mode="single" />
                <Export enabled={true} allowExportSelectedData={true} />
                <ColumnChooser enabled={true} />
                <Editing
                    mode="cell"
                    refreshMode="repaint"
                    allowUpdating={true}
                    allowAdding={true}
                />
                <FilterRow visible={true} />
                <Column dataField="chargeCode" caption="Charge Code" allowSorting={false}
                    editCellRender={(cellElement) =>
                        RenderSelectBoxCell({
                            loadUrl: `${window._env_.REACT_APP_APIS}/wm/Lookup/GetTransactionCharges`,
                            loadParams: {
                                chargeTypes: function () {
                                    return 'T';
                                }
                            },
                            key: "id",
                            displayExpr: "name",
                            valueExpr: "id",
                            accessToken: accessToken,
                            defaultValue: cellElement.data.rCharge,
                            sort: [{ selector: "name", desc: false }],
                            onValueChangeCallback: (value, text, additionalData) => {
                                cellElement.setValue(({ value, text, additionalData }));
                            },
                        })
                    }
                    setCellValue={(rowData, newValue) => {
                        rowData.rCharge = newValue.value;
                        rowData.chargeCode = newValue.text;
                        if (newValue.additionalData) {
                            rowData.description = newValue.additionalData.description || rowData.description;
                            rowData.chargeuom = newValue.additionalData.chargeuom || rowData.chargeuom;
                        }
                    }}
                >
                    <RequiredRule message="Charge is required." />
                </Column>
                <Column dataField="description" caption="Description" allowEditing={true} />
                <Column dataField="quantitybillas" caption="Bill As" dataType="number" format="#,##0.00" />
                <Column dataField="quantitybillasuom" caption="UOM" dataType="string" />
                <Column dataField="rate" caption="Rate" dataType="number" 
                    format={{
                        type: 'currency',
                        precision: 2,
                        currency: 'USD',
                        useGrouping: true
                    }} />
                <Column dataField="amount" caption="Amount" dataType="number" 
                    format={{
                        type: 'currency',
                        precision: 2,
                        currency: 'USD',
                        useGrouping: true
                    }} allowEditing={false} />
                <Column  dataField="userdefinedcode1" caption="Code1" dataType="string" allowSorting={false}
                    editCellRender={(cellElement) =>
                        RenderSelectBoxCell({
                            loadUrl: `${window._env_.REACT_APP_APIS}/${data.WMLookup.GetCodes}`,
                            loadParams: {
                                codeType: function () {
                                    return '36';
                                }
                            },
                            key: "name",
                            displayExpr: "name",
                            valueExpr: "name",
                            accessToken: accessToken,
                            sort: [{ selector: "name", desc: false }],
                            defaultValue: cellElement.data.userdefinedcode1,
                            onValueChangeCallback: (value, text) => {
                                cellElement.setValue(({ value, text }));
                            },
                        })
                    }
                    setCellValue={(rowData, newValue) => {
                        rowData.userdefinedcode1 = newValue.text;
                    }}
                />
                <Column  dataField="userdefinedcode2" caption="Code2" dataType="string" allowSorting={false}
                    editCellRender={(cellElement) =>
                        RenderSelectBoxCell({
                            loadUrl: `${window._env_.REACT_APP_APIS}/${data.WMLookup.GetCodes}`,
                            loadParams: {
                                codeType: function () {
                                    return '37';
                                }
                            },
                            key: "name",
                            displayExpr: "name",
                            valueExpr: "name",
                            accessToken: accessToken,
                            sort: [{ selector: "name", desc: false }],
                            defaultValue: cellElement.data.userdefinedcode2,
                            onValueChangeCallback: (value, text) => {
                                cellElement.setValue(({ value, text }));
                            },
                        })
                    }
                    setCellValue={(rowData, newValue) => {
                        rowData.userdefinedcode2 = newValue.text;
                    }}
                />
                <Column  dataField="userdefinedcode3" caption="Code3" dataType="string" allowSorting={false}
                    editCellRender={(cellElement) =>
                        RenderSelectBoxCell({
                            loadUrl: `${window._env_.REACT_APP_APIS}/${data.WMLookup.GetCodes}`,
                            loadParams: {
                                codeType: function () {
                                    return '38';
                                }
                            },
                            key: "name",
                            displayExpr: "name",
                            valueExpr: "name",
                            accessToken: accessToken,
                            sort: [{ selector: "name", desc: false }],
                            defaultValue: cellElement.data.userdefinedcode3,
                            onValueChangeCallback: (value, text) => {
                                cellElement.setValue(({ value, text }));
                            },
                        })
                    }
                    setCellValue={(rowData, newValue) => {
                        rowData.userdefinedcode3 = newValue.text;
                    }}
                />
                <Column  dataField="userdefinedcode4" caption="Code4" dataType="string" allowSorting={false}
                    editCellRender={(cellElement) =>
                        RenderSelectBoxCell({
                            loadUrl: `${window._env_.REACT_APP_APIS}/${data.WMLookup.GetCodes}`,
                            loadParams: {
                                codeType: function () {
                                    return '39';
                                }
                            },
                            key: "name",
                            displayExpr: "name",
                            valueExpr: "name",
                            accessToken: accessToken,
                            sort: [{ selector: "name", desc: false }],
                            defaultValue: cellElement.data.userdefinedcode4,
                            onValueChangeCallback: (value, text) => {
                                cellElement.setValue(({ value, text }));
                            },
                        })
                    }
                    setCellValue={(rowData, newValue) => {
                        rowData.userdefinedcode4 = newValue.text;
                    }}
                />
                <Column visible={false} dataField="userdefineddate1" caption="Date1" dataType="date" allowSorting={false} />
                <Column visible={false} dataField="userdefineddate2" caption="Date2" dataType="date" allowSorting={false} />
                <Column visible={false} dataField="userdefineddate3" caption="Date3" dataType="date" allowSorting={false} />
                <Column visible={false} dataField="userdefineddate4" caption="Date4" dataType="date" allowSorting={false} />
                <Column visible={false} dataField="userdefinedquantity1" caption="Quantity1" dataType="number" format="#,##0.00" allowSorting={false} />
                <Column visible={false} dataField="userdefinedquantity2" caption="Quantity2" dataType="number" format="#,##0.00" allowSorting={false} />
                <Column visible={false} dataField="userdefinedquantity3" caption="Quantity3" dataType="number" format="#,##0.00" allowSorting={false} />
                <Column visible={false} dataField="userdefinedquantity4" caption="Quantity4" dataType="number" format="#,##0.00" allowSorting={false} />
                <Column visible={false} dataField="userdefinedreference1" caption="Reference1" dataType="string" allowSorting={false} />
                <Column visible={false} dataField="userdefinedreference2" caption="Reference2" dataType="string" allowSorting={false} />
                <Column visible={false} dataField="userdefinedcomment" caption="Comment" dataType="string" allowSorting={false} />
                <DataGridToolbar>
                    <DataGridToolbarItem name="addRowButton" />
                    <DataGridToolbarItem location="after">
                        <Button
                            onClick={deleteRecords}
                            icon="trash"
                            disabled={!selectedItemKeys.length}
                        />
                    </DataGridToolbarItem>
                    <DataGridToolbarItem location="after">
                        <GridLayoutManager gridKey={gridKey} dataGrid={chargesGridRef} setCurrentLayout={setCurrentLayout} />
                    </DataGridToolbarItem>
                    <DataGridToolbarItem name="exportButton" />
                    <DataGridToolbarItem name="columnChooserButton" />
                </DataGridToolbar>
            </DataGrid>
        </div>
    );
};

export default TransactionChargesGrid;

